import type { Country, Locale } from '@/types/regionTypes';

import {
  APOLLO_CLIENTS,
  CustomContext,
  initializeApollo,
} from '@/graphql/client';
import type {
  ContentfulDefaultHeaderSettingsQuery,
  ContentfulDefaultHeaderSettingsQueryVariables,
  ContentfulDefaultLayout,
  ContentfulDefaultLayoutQuery,
  ContentfulDefaultLayoutQueryVariables,
  ContentfulDetailPageQuery,
  ContentfulDetailPageQueryVariables,
  ContentfulDictionaryItem,
  ContentfulDictionaryQuery,
  ContentfulDictionaryQueryVariables,
  ContentfulFixedPageQuery,
  ContentfulFixedPageQueryVariables,
  ContentfulGetAllNewsPagesQuery,
  ContentfulGetAllNewsPagesQueryVariables,
  ContentfulListingPageQuery,
  ContentfulListingPageQueryVariables,
  ContentfulPage,
  ContentfulPageBoutiqueDetailCollectionBySlugAndTrackingKeyQuery,
  ContentfulPageBoutiqueDetailCollectionBySlugAndTrackingKeyQueryVariables,
  ContentfulPageBreadcrumbFragment,
  ContentfulPageExhibitionHistoriesFaqQuery,
  ContentfulPageExhibitionHistoriesFaqQueryVariables,
  ContentfulPageQuery,
  ContentfulPageQueryVariables,
  ContentfulPlaceholderServiceCollectionByTypeQuery,
  ContentfulPlaceholderServiceCollectionByTypeQueryVariables,
  ContentfulSelectorModalByMarketingPartnerQuery,
  ContentfulSelectorModalByMarketingPartnerQueryVariables,
  ContentfulSeoConfiguration,
  ContentfulServiceQuoteDetailPageQuery,
  ContentfulServiceQuoteDetailPageQueryVariables,
  ContentfulSysFragment,
} from '@/graphql/contentful/api';
import {
  type ContentfulPageBoutiqueListCollectionQuery,
  type ContentfulPageBoutiqueListCollectionQueryVariables,
  DefaultHeaderSettingsDocument,
  DefaultLayoutDocument,
  DetailPageDocument,
  DictionaryDocument,
  FixedPageDocument,
  GetAllNewsPagesDocument,
  ListingPageDocument,
  PageBoutiqueDetailCollectionBySlugAndTrackingKeyDocument,
  PageBoutiqueListCollectionQueryDocument,
  PageDocument,
  PageExhibitionHistoriesFaqDocument,
  PlaceholderServiceCollectionByTypeDocument,
  SelectorModalByMarketingPartnerDocument,
  ServiceQuoteDetailPageDocument,
} from '@/graphql/contentful/api';
import type { LanguageCodeEnum } from '@/graphql/saleor/api';
import {
  fetchContentfulExtraData,
  handleDuplicateBreadcrumbs,
} from '@/integrations/contentful/helpers';
import type {
  ContentfulData,
  ContentfulFixedPageCMSData,
} from '@/integrations/contentful/types';
import type { CMSLayoutDataProps } from '@/layouts/Default';
import { BoutiqueDetailCMSTypeEnum } from '@/lib/boutique/helpers';
import { getBrandConfig } from '@/lib/configuration/configuration';
import { captureError } from '@/lib/errors';
import type { LocaleMessages } from '@/lib/intl';
import { fetchSaleorProductSkus } from '@/lib/productSkus';
import { SeoConfigBuilder } from '@/lib/seo/SeoVariables';
import type { Maybe } from '@/lib/types';
import { translateStrapMaterial } from '@/views/StrapMaterialView/helpers';

export const HOME_SLUG = '/';

export type DetailPageType = 'watch' | 'strap' | 'eyewear';

export const filterContentfulCollection = (items: unknown[] | undefined) => {
  const allItems = items;

  return (allItems?.filter((item) => {
    return !!(item as Maybe<{ sys: ContentfulSysFragment }>)?.sys?.id;
  }) || []) as ContentfulData[];
};

export const fetchCMSPlaceholderServiceData = async (
  type: string,
  languageCode: string,
) => {
  const client = initializeApollo();
  const { data } = await client.query<
    ContentfulPlaceholderServiceCollectionByTypeQuery,
    ContentfulPlaceholderServiceCollectionByTypeQueryVariables
  >({
    query: PlaceholderServiceCollectionByTypeDocument,
    variables: {
      type,
      languageCode,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });
  return data.placeholderServiceCollection?.items?.[0];
};

export const getBreadcrumbSlug = (slug: string): Array<string> => {
  const pageList = slug.split('/');
  const slugArr: Array<string> = [];
  let path = '';

  for (let i = 0; i < pageList.length; i++) {
    const page = pageList[i];
    if (i === 0) {
      path = page;
    } else {
      path = `${path}/${pageList[i]}`;
    }
    slugArr.push(path);
  }

  //add home as root
  if (!slugArr?.includes(HOME_SLUG)) {
    slugArr.unshift(HOME_SLUG);
  }

  return slugArr;
};

export const sortBreadcrumb = (
  breadcrumbList: ContentfulPageBreadcrumbFragment[],
): ContentfulPageBreadcrumbFragment[] => {
  const home = breadcrumbList.find(function (item) {
    return item.slug === HOME_SLUG;
  });
  const arr = breadcrumbList
    .filter(function (item) {
      return item.slug !== HOME_SLUG;
    })
    .sort(function (a, b) {
      return (a.slug?.length || 0) > (b.slug?.length || 0) ? 1 : -1;
    });
  if (home) {
    arr.unshift(home);
  }
  return arr;
};

export const fetchCMSPageData = async (
  slug: string,
  languageCode: string,
  countryCode: string,
  isPreview?: boolean | null | undefined,
  type?: PageType | null,
) => {
  const client = initializeApollo();
  const parentSlugs = getBreadcrumbSlug(slug);

  const { data } = await client.query<
    ContentfulPageQuery,
    ContentfulPageQueryVariables
  >({
    query: PageDocument,
    variables: {
      slug,
      languageCode,
      parentSlugs,
      countryCode,
      preview: isPreview,
      type: type ?? null,
    },
    context: CustomContext({
      disableErrorNotification: true,
      client: APOLLO_CLIENTS.contentful,
    }),
  });

  const cmsData = filterContentfulCollection(
    data.pageCollection?.items?.[0]?.contentCollection?.items,
  );

  const pageCollectionItems = data.pageCollection?.items?.[0];
  const seoConfig =
    pageCollectionItems?.seoConfiguration as ContentfulSeoConfiguration;
  const seoConfigOverride =
    pageCollectionItems?.seoConfigurationOverride as ContentfulSeoConfiguration;
  const seoConfigBuilder = new SeoConfigBuilder(seoConfig, seoConfigOverride);

  const mainHeader = data.pageCollection?.items?.[0]?.mainHeader;
  const breadcrumb = handleDuplicateBreadcrumbs(data.breadcrumb?.items);

  const pageTitle = pageCollectionItems?.pageTitle;
  const shortDescription = pageCollectionItems?.shortDescription;
  const pageType = pageCollectionItems?.type;
  const tagsCollection = pageCollectionItems?.tagsCollection;
  const publishDate = pageCollectionItems?.publishDate;
  const simpleFooter = data?.pageCollection?.items?.[0]?.simpleFooter;
  const footer = data?.pageCollection?.items?.[0]?.footer;

  return {
    cmsData,
    mainHeader,
    breadcrumb,
    seoConfigBuilder,
    pageTitle,
    shortDescription,
    pageType,
    tagsCollection,
    publishDate,
    simpleFooter,
    footer,
  };
};

export const fetchCMSPageExhibitionHistoriesFaqData = async (
  slug: string,
  languageCode: string,
  countryCode: string,
  isPreview?: boolean | null | undefined,
  type?: PageType | null,
) => {
  const client = initializeApollo();
  const parentSlugs = getBreadcrumbSlug(slug);

  const { data } = await client.query<
    ContentfulPageExhibitionHistoriesFaqQuery,
    ContentfulPageExhibitionHistoriesFaqQueryVariables
  >({
    query: PageExhibitionHistoriesFaqDocument,
    variables: {
      slug,
      languageCode,
      parentSlugs,
      countryCode,
      preview: isPreview,
      type: type ?? null,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });
  const cmsData = filterContentfulCollection(
    data.pageExhibitionHistoriesFaqCollection?.items?.[0]?.contentCollection
      ?.items,
  );

  const pageCollectionItems =
    data.pageExhibitionHistoriesFaqCollection?.items?.[0];
  const seoConfig =
    pageCollectionItems?.seoConfiguration as ContentfulSeoConfiguration;
  const seoConfigOverride =
    pageCollectionItems?.seoConfigurationOverride as ContentfulSeoConfiguration;
  const seoConfigBuilder = new SeoConfigBuilder(seoConfig, seoConfigOverride);

  const mainHeader =
    data.pageExhibitionHistoriesFaqCollection?.items?.[0]?.mainHeader;
  const previousBreadcrumbItems = handleDuplicateBreadcrumbs(
    data.breadcrumb?.items,
  );
  const breadcrumb = [
    ...previousBreadcrumbItems,
    {
      slug: data.pageExhibitionHistoriesFaqCollection?.items[0]?.slug,
      pageTitle: data.pageExhibitionHistoriesFaqCollection?.items[0]?.pageTitle,
    },
  ];

  return {
    cmsData,
    mainHeader,
    breadcrumb,
    seoConfigBuilder,
  };
};

export const fetchCMSDefaultLayoutData = async (
  locale: Locale,
  country: Country,
  disableErrorNotification?: boolean,
  isPreview?: boolean | null | undefined,
) => {
  const client = initializeApollo();

  const { data } = await client.query<
    ContentfulDefaultLayoutQuery,
    ContentfulDefaultLayoutQueryVariables
  >({
    query: DefaultLayoutDocument,
    variables: {
      languageCode: locale.locale,
      countryCode: country.slug,
      preview: isPreview,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
      disableErrorNotification,
    },
  });

  return data.defaultLayoutCollection?.items[0];
};
export const fetchCmsPageDataWithExtraData = async (
  slug: string,
  locale: string,
  languageCode: LanguageCodeEnum,
  channel: string,
  countryCode: string,
  isPreview?: boolean | null | undefined,
) => {
  try {
    const type = getTypeByPageSlug(slug);

    const dataByPage = await fetchCMSPageData(
      slug,
      locale,
      countryCode,
      isPreview,
      type,
    );

    let cmsDataGroup = dataByPage;
    if (dataByPage.cmsData.length === 0) {
      const dataByPageExhibitionHistoriesFaqData =
        await fetchCMSPageExhibitionHistoriesFaqData(
          slug,
          locale,
          countryCode,
          isPreview,
          type,
        );
      if (dataByPageExhibitionHistoriesFaqData.cmsData.length > 0) {
        cmsDataGroup = {
          cmsData: dataByPageExhibitionHistoriesFaqData.cmsData,
          mainHeader: dataByPageExhibitionHistoriesFaqData.mainHeader,
          breadcrumb: dataByPageExhibitionHistoriesFaqData.breadcrumb,
          seoConfigBuilder:
            dataByPageExhibitionHistoriesFaqData.seoConfigBuilder,
          pageTitle: '',
          shortDescription: '',
          publishDate: '',
          pageType: null,
          tagsCollection: { items: [] },
          simpleFooter: {},
          footer: null,
        };
      }
    }

    const {
      breadcrumb,
      cmsData,
      footer,
      mainHeader,
      pageTitle,
      pageType,
      publishDate,
      seoConfigBuilder,
      shortDescription,
      simpleFooter,
      tagsCollection,
    } = cmsDataGroup;
    if (cmsData.length === 0) {
      return {
        cmsData: [],
        cmsExtraData: {},
        cmsDefaultLayoutData:
          mainHeader || simpleFooter || footer
            ? {
                ...(mainHeader && { header: mainHeader }),
                ...(simpleFooter && { footer: simpleFooter }),
                ...(footer && { footer }),
              }
            : undefined,
      };
    }
    let cmsExtraData = {};
    if (!getBrandConfig().disableSaleorApiCall) {
      cmsExtraData = await fetchContentfulExtraData(
        cmsData,
        languageCode,
        channel,
        isPreview ?? undefined,
      );
    }
    const { saleorProductSkus, saleorProductSkusProductVariant } =
      await fetchSaleorProductSkus(cmsData, languageCode, channel);

    return {
      cmsData: cmsData,
      cmsExtraData: cmsExtraData,
      cmsDefaultLayoutData:
        mainHeader || simpleFooter || footer
          ? {
              ...(mainHeader && { header: mainHeader }),
              ...(simpleFooter && { footer: simpleFooter }),
              ...(footer && { footer }),
            }
          : undefined,
      breadcrumb,
      seoConfigBuilder: seoConfigBuilder ?? null,
      pageTitle,
      shortDescription,
      publishDate,
      pageType,
      tagsCollection,
      saleorProductSkus,
      saleorProductSkusProductVariant,
    };
  } catch (error) {
    console.error('Apollo query failed or unsupported locale', error);

    return {
      cmsData: [],
      cmsExtraData: {},
      cmsDefaultLayoutData: {},
    };
  }
};

export const fetchDetailPageData = async (
  type: DetailPageType,
  sku: string,
  locale: string,
  collectionName: string,
  modelName: string,
  countryCode?: string,
  isPreview?: boolean | null | undefined,
): Promise<{
  cmsData: ContentfulData[];
  cmsDefaultLayoutData?: CMSLayoutDataProps | null;
  seoConfigBuilder: SeoConfigBuilder | null;
}> => {
  const client = initializeApollo();
  collectionName =
    collectionName.charAt(0).toUpperCase() + collectionName.slice(1);

  const { data } = await client.query<
    ContentfulDetailPageQuery,
    ContentfulDetailPageQueryVariables
  >({
    query: DetailPageDocument,
    variables: {
      type,
      sku: sku,
      modelName,
      collectionName,
      languageCode: locale,
      preview: isPreview,
      countryCode,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });

  let detailPageCollection;

  if (sku) {
    detailPageCollection = data.detailPageCollection?.items.find(
      (item) =>
        item?.watchSkus?.includes(sku) || item?.strapSkus?.includes(sku),
    );
  }

  if (!detailPageCollection && modelName) {
    detailPageCollection = data.detailPageCollection?.items.find(
      (item) => item?.model === modelName,
    );
  }

  if (!detailPageCollection && collectionName) {
    detailPageCollection = data.detailPageCollection?.items.find(
      (item) => item?.watchCollection === collectionName,
    );
  }

  if (!detailPageCollection) {
    detailPageCollection = data.detailPageCollection?.items[0];
  }

  const seoConfigBuilder = new SeoConfigBuilder(
    data.detailPageCollection?.items[0]
      ?.seoConfiguration as ContentfulSeoConfiguration,
    data.detailPageCollection?.items[0]
      ?.seoConfigurationOverride as ContentfulSeoConfiguration,
    {
      productName: modelName,
      referenceNumber: sku,
    },
  );

  const filteredContentfulCollection = filterContentfulCollection(
    detailPageCollection?.contentCollection?.items,
  );

  const header = data.detailPageCollection?.items?.[0]?.mainHeader;
  const footer = data.detailPageCollection?.items?.[0]?.footer;

  return {
    cmsData: filteredContentfulCollection,
    cmsDefaultLayoutData:
      header || footer
        ? {
            ...(header && { header }),
            ...(footer && { footer }),
          }
        : undefined,
    seoConfigBuilder: seoConfigBuilder ?? null,
  };
};

export const fetchDetailPageDataWithExtraData = async (
  type: DetailPageType,
  sku: string,
  locale: string,
  collectionName: string,
  modelName: string,
  languageCode: LanguageCodeEnum,
  channel: string,
  localeTag?: string,
  isPreview?: boolean | null | undefined,
) => {
  const { cmsData, cmsDefaultLayoutData, seoConfigBuilder } =
    await fetchDetailPageData(
      type,
      sku,
      locale,
      collectionName,
      modelName,
      localeTag,
      isPreview,
    );
  const cmsExtraData = await fetchContentfulExtraData(
    cmsData,
    languageCode,
    channel,
    isPreview ?? undefined,
  );
  return {
    cmsData: cmsData,
    cmsExtraData: cmsExtraData,
    cmsDefaultLayoutData,
    seoConfigBuilder: seoConfigBuilder,
  };
};

export const fetchListingPageData = async (
  type:
    | 'all the watches'
    | 'all the watches filtered'
    | 'all the straps'
    | 'all the eyewear'
    | 'watch editorial page'
    | 'watch collection'
    | 'strap material',
  locale: string,
  collection?: string | null | undefined,
  editorialPageName?: string | null | undefined,
  strapMaterial?: string | null | undefined,
  countryCode?: string,
  languageCode?: LanguageCodeEnum,
  isPreview?: boolean | null | undefined,
): Promise<{
  algoliaCategoryId: string;
  algoliaRuleName: string;
  cmsData: ContentfulData[];
  cmsDefaultLayoutData?: CMSLayoutDataProps | null;
  seoConfigBuilder: SeoConfigBuilder | null;
}> => {
  const client = initializeApollo();

  const { data } = await client.query<
    ContentfulListingPageQuery,
    ContentfulListingPageQueryVariables
  >({
    query: ListingPageDocument,
    variables: {
      preview: isPreview,
      type,
      collectionItemsLimit: 20,
      languageCode: locale,
      collectionName: collection,
      editorialPageName: editorialPageName,
      strapMaterial: strapMaterial,
      countryCode,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });
  const { algoliaCategoryId, algoliaRuleName } =
    data.listingPageCollection?.items?.[0] || {};

  const seoConfigBuilder = new SeoConfigBuilder(
    data.listingPageCollection?.items[0]
      ?.seoConfiguration as ContentfulSeoConfiguration,
    data.listingPageCollection?.items[0]
      ?.seoConfigurationOverride as ContentfulSeoConfiguration,
    {
      collectionName: collection ?? '',
      strapMaterial: languageCode
        ? await translateStrapMaterial(languageCode, strapMaterial)
        : '',
    },
  );

  const filteredContentfulCollection = filterContentfulCollection(
    data.listingPageCollection?.items?.[0]?.contentCollection?.items,
  );

  const header = data.listingPageCollection?.items?.[0]?.mainHeader;
  const footer = data.listingPageCollection?.items?.[0]?.footer;

  return {
    algoliaCategoryId: algoliaCategoryId ?? '',
    cmsData: filteredContentfulCollection,
    cmsDefaultLayoutData:
      header || footer
        ? {
            ...(header && { header }),
            ...(footer && { footer }),
          }
        : undefined,
    algoliaRuleName: algoliaRuleName ?? '',
    seoConfigBuilder: seoConfigBuilder ?? null,
  };
};

export const fetchDictionary = async (
  locale: string,
  isPreview?: boolean | null | undefined,
): Promise<Array<ContentfulDictionaryItem | null>> => {
  const client = initializeApollo();
  const { data } = await client.query<
    ContentfulDictionaryQuery,
    ContentfulDictionaryQueryVariables
  >({
    query: DictionaryDocument,
    variables: {
      limit: 1000,
      skip: 0,
      locale,
      preview: isPreview,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });
  return (
    (data?.dictionaryCollection?.items?.[0]?.contentCollection
      ?.items as Array<ContentfulDictionaryItem | null>) ?? []
  );
};

export const fetchSelectorModalByMarketingPartner = async (
  marketingPartner: string,
  draftMode = false,
  locale: string,
): Promise<
  | NonNullable<
      ContentfulSelectorModalByMarketingPartnerQuery['selectorCollection']
    >['items'][0]
  | null
> => {
  const client = initializeApollo();
  const { data } = await client.query<
    ContentfulSelectorModalByMarketingPartnerQuery,
    ContentfulSelectorModalByMarketingPartnerQueryVariables
  >({
    query: SelectorModalByMarketingPartnerDocument,
    variables: {
      marketingPartner,
      preview: draftMode,
      languageCode: locale,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });

  return data?.selectorCollection?.items?.[0] ?? null;
};

export const fetchServiceQuoteDetailPageData = async (
  locale: string,
  countryCode?: string,
): Promise<ContentfulData[]> => {
  const client = initializeApollo();
  const { data } = await client.query<
    ContentfulServiceQuoteDetailPageQuery,
    ContentfulServiceQuoteDetailPageQueryVariables
  >({
    query: ServiceQuoteDetailPageDocument,
    variables: {
      languageCode: locale,
      countryCode,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });
  return await filterContentfulCollection(
    data.pageServiceQuoteDetailCollection?.items?.[0]?.contentCollection?.items,
  );
};
export const fetchFixedPageData = async (
  slug: string,
  locale: string,
  languageCode: LanguageCodeEnum,
  channel: string,
  countryCode?: string,
  isPreview?: boolean | null | undefined,
): Promise<{
  cms: ContentfulFixedPageCMSData;
  dictionary: ContentfulDictionaryItem[];
  seoConfigBuilder: SeoConfigBuilder;
}> => {
  const client = initializeApollo();
  const { data } = await client.query<
    ContentfulFixedPageQuery,
    ContentfulFixedPageQueryVariables
  >({
    query: FixedPageDocument,
    variables: {
      slug: slug,
      languageCode: locale,
      countryCode,
      preview: isPreview,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });

  const topContent = filterContentfulCollection(
    data.pageFixedCollection?.items?.[0]?.topContentCollection?.items,
  );
  const bottomContent = filterContentfulCollection(
    data.pageFixedCollection?.items?.[0]?.bottomContentCollection?.items,
  );

  const pinnedNews =
    data.pageFixedCollection?.items?.[0]?.pinnedNewsCollection?.items?.filter(
      (item): item is ContentfulPage => item !== null,
    ) ?? [];

  const pageCollectionItems = data.pageFixedCollection?.items?.[0];
  const seoConfig =
    pageCollectionItems?.seoConfiguration as ContentfulSeoConfiguration;
  const seoConfigOverride =
    pageCollectionItems?.seoConfigurationOverride as ContentfulSeoConfiguration;
  const seoConfigBuilder = new SeoConfigBuilder(seoConfig, seoConfigOverride);
  const dictionary = (data.pageFixedCollection?.items?.[0]?.dictionaryCollection
    ?.items || []) as ContentfulDictionaryItem[];

  const cmsExtraData = await fetchContentfulExtraData(
    [...topContent, ...bottomContent],
    languageCode,
    channel,
    isPreview ?? undefined,
  );

  const { saleorProductSkus, saleorProductSkusProductVariant } =
    await fetchSaleorProductSkus(
      [...topContent, ...bottomContent],
      languageCode,
      channel,
    );

  return {
    cms: {
      topContent,
      bottomContent,
      cmsExtraData,
      pinnedNews,
      saleorProductSkus,
      saleorProductSkusProductVariant,
    },
    dictionary,
    seoConfigBuilder,
  };
};

export async function getLocaleMessages(
  locale: string,
  isPreview?: boolean | null,
): Promise<LocaleMessages> {
  let data: Array<ContentfulDictionaryItem | null> = [];
  try {
    data = await fetchDictionary(locale, isPreview);
  } catch (e) {
    captureError(new Error('fetch contentful locale messages failed'));
  }
  const messages: Record<string, string> = {};
  data?.forEach((item) => {
    if (item?.key && item?.value) {
      messages[item.key] = item.value;
    }
  });

  return messages;
}

export async function fetchCMSDefaultConfig(
  locale: Locale,
  country: Country,
  isPreview?: boolean | null,
): Promise<{
  cmsDefaultLayoutData: ContentfulDefaultLayout;
  localeMessages: LocaleMessages;
}> {
  const localeMessages = await getLocaleMessages(locale.locale, isPreview);
  const cmsDefaultLayoutData = ((await fetchCMSDefaultLayoutData(
    locale,
    country,
    true,
    isPreview,
  )) ?? {}) as ContentfulDefaultLayout;
  return {
    localeMessages,
    cmsDefaultLayoutData,
  };
}

export async function fetchCMSDefaultHeaderConfig(
  locale: Locale,
  country: Country,
  isPreview?: boolean | null,
  disableErrorNotification?: boolean,
): Promise<{
  defaultHeaderSettings: ContentfulDefaultLayout;
  localeMessages: LocaleMessages;
}> {
  const localeMessages = await getLocaleMessages(locale.locale, isPreview);
  const client = initializeApollo();
  const { data } = await client.query<
    ContentfulDefaultHeaderSettingsQuery,
    ContentfulDefaultHeaderSettingsQueryVariables
  >({
    query: DefaultHeaderSettingsDocument,
    variables: {
      languageCode: locale.locale,
      countryCode: country.slug,
      preview: isPreview,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
      disableErrorNotification,
    },
  });

  const defaultHeaderSettings = (data.defaultLayoutCollection?.items[0] ??
    {}) as ContentfulDefaultLayout;

  return {
    defaultHeaderSettings,
    localeMessages,
  };
}

type PageType = 'Homepage' | 'News' | 'Histories' | 'Legal' | 'Editorial';

export const getTypeByPageSlug = (slug: string): PageType | null => {
  if (slug === '/') {
    return 'Homepage';
  }
  // implement other page type here
  return null;
};
export const fetchCMSBoutiqueDetailData = async (
  isDefault: boolean,
  countryCode?: string,
  locale?: string | null,
  type?: BoutiqueDetailCMSTypeEnum,
  trackingKey?: string | null,
) => {
  const client = initializeApollo();
  const variables: ContentfulPageBoutiqueDetailCollectionBySlugAndTrackingKeyQueryVariables =
    isDefault ? { default: isDefault } : { trackingKey: trackingKey };
  const params =
    type === BoutiqueDetailCMSTypeEnum.Detail
      ? {
          ...variables,
          countryCode,
          locale,
          type,
        }
      : {
          default: isDefault,
          countryCode,
          locale,
          type,
          trackingKey: trackingKey,
        };
  const { data } = await client.query<
    ContentfulPageBoutiqueDetailCollectionBySlugAndTrackingKeyQuery,
    ContentfulPageBoutiqueDetailCollectionBySlugAndTrackingKeyQueryVariables
  >({
    query: PageBoutiqueDetailCollectionBySlugAndTrackingKeyDocument,
    variables: { ...params },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });

  const seoConfigBuilder = new SeoConfigBuilder(
    data.pageBoutiqueDetailCollection?.items[0]
      ?.seoConfiguration as ContentfulSeoConfiguration,
    data.pageBoutiqueDetailCollection?.items[0]
      ?.seoConfigurationOverride as ContentfulSeoConfiguration,
  );

  return {
    PageBoutiqueDetailData: { data },
    seoConfigBuilder: seoConfigBuilder ?? null,
  };
};

export const fetchCMSDefaultPageBoutiqueListCollectionData = async (
  selectedCountry?: string,
  isDefault?: boolean,
  isPreview?: boolean | null | undefined,
  localeTag?: string,
  locale?: string,
) => {
  const client = initializeApollo();
  const { data } = await client.query<
    ContentfulPageBoutiqueListCollectionQuery,
    ContentfulPageBoutiqueListCollectionQueryVariables
  >({
    query: PageBoutiqueListCollectionQueryDocument,
    variables: {
      selectedCountry,
      isDefault,
      preview: isPreview,
      countryCode: localeTag,
      locale: locale,
    },
    context: {
      client: APOLLO_CLIENTS.contentful,
    },
  });
  const seoConfigBuilder = new SeoConfigBuilder(
    data.pageBoutiqueListCollection?.items[0]
      ?.seoConfiguration as ContentfulSeoConfiguration,
    data.pageBoutiqueListCollection?.items[0]
      ?.seoConfigurationOverride as ContentfulSeoConfiguration,
  );

  return {
    PageBoutiqueListCollectionData: { data },
    seoConfigBuilder: seoConfigBuilder ?? null,
  };
};

export async function fetchNewsPages(
  languageCode: string,
  countryCode: string,
) {
  const client = initializeApollo();
  const { data } = await client.query<
    ContentfulGetAllNewsPagesQuery,
    ContentfulGetAllNewsPagesQueryVariables
  >({
    query: GetAllNewsPagesDocument,
    variables: {
      languageCode,
      countryCode,
    },
    context: CustomContext({
      disableErrorNotification: true,
      client: APOLLO_CLIENTS.contentful,
    }),
  });

  return data;
}
